import { initializeApp } from 'firebase/app';
import { getAuth,
  RecaptchaVerifier,
  PhoneAuthProvider,
  MultiFactorResolver,
  PhoneMultiFactorGenerator,
  MultiFactorInfo,
  PhoneMultiFactorInfo,
  UserCredential,
  User,
  multiFactor,
  Auth } from 'firebase/auth';
import type { FirebaseApp } from 'firebase/app';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject } from 'rxjs';
import { sendToRaygun } from '../main/error-handlers/global-error-handler.service';

@Injectable()
export class FirebaseService {

  public app: FirebaseApp;

  public recaptchaSolved = new BehaviorSubject<boolean>(false);

  constructor() {
    this.app = initializeApp(environment.firebase);

    // Browser checks only: e2e/lib/login.ts sets the flag before the app loads, so Firebase skips its reCAPTCHA for the check's
    // test users. Never in a production build (production: true), never for a person's own browser (no flag).
    if (!environment.production && (window as any).__TOWNIP_E2E__ === true) {
      getAuth().settings.appVerificationDisabledForTesting = true;
    }
  }

  get auth(): Auth {
    return getAuth();
  }

  public initInvisibleCaptchaVerifier(buttonId: string): RecaptchaVerifier {
    this.recaptchaSolved.next(false);

    return new RecaptchaVerifier(this.auth, buttonId, {
      size: 'invisible',
      callback: (response: string) => {
        this.recaptchaSolved.next(true);
      },
      'expired-callback': () => {
        this.recaptchaSolved.next(false);
      },
      'error-callback': () => {
        this.recaptchaSolved.next(false);
      }
    });
  }

  public async sendMfaCode(
    resolver: MultiFactorResolver,
    recaptchaVerifier?: RecaptchaVerifier
  ): Promise<string> {
    const phoneInfoOptions = {
      multiFactorHint: resolver.hints[0],
      session: resolver.session
    };

    const phoneAuthProvider = new PhoneAuthProvider(this.auth);
    return phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
  }

  public async sendMfaCodeForEnrollment(appVerifier: RecaptchaVerifier, phone: string): Promise<string> {
    if (!this.auth.currentUser) throw new Error('No user signed in');

    const mfaSession = await multiFactor(this.auth.currentUser).getSession();

    const phoneAuthProvider = new PhoneAuthProvider(this.auth);
    const phoneInfoOptions = {
      phoneNumber: phone,
      session: mfaSession
    };

    return phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, appVerifier);
  }

  public async verifyMfaEnroll(verificationId: string, verificationCode: string): Promise<void> {
    if (!this.auth.currentUser) throw new Error('No user signed in');

    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

    return multiFactor(this.auth.currentUser).enroll(multiFactorAssertion);
  }

  public async verifyMfaSignIn(
    resolver: MultiFactorResolver,
    verificationId: string,
    verificationCode: string
  ): Promise<UserCredential> {
    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

    return resolver.resolveSignIn(multiFactorAssertion);
  }

  public getCurrentUser(): User | null {
    return this.auth.currentUser;
  }

  public removePhoneMFA(): Promise<void> {
    if (!this.auth.currentUser) throw new Error('No user signed in');

    const factor = this.getActiveMFAs().find((mfa) => mfa.factorId === 'phone');
    if (!factor) throw new Error('No phone MFA found');

    return multiFactor(this.auth.currentUser).unenroll(factor);
  }

  public getActiveMFAs(): MultiFactorInfo[] {
    if (!this.auth.currentUser) return [];
    return multiFactor(this.auth.currentUser).enrolledFactors;
  }

  public getActiveMfaNumber(): string {
    if (!this.auth.currentUser) return '';

    const mfas = multiFactor(this.auth.currentUser).enrolledFactors as PhoneMultiFactorInfo[];

    if (mfas?.length > 0) {
      return mfas[0].phoneNumber;
    }

    return '';
  }

  public translateErrorCode(errorCode: string, custom?: string): string {
    let msg: string = '';
    let noMatch = false;

    switch (errorCode) {
    case 'auth/invalid-verification-code':
      msg = 'That code is not valid. Resend Code if needed.';
      break;
    case 'auth/missing-verification-code':
      msg = 'Verification code is required.';
      break;
    case 'auth/code-expired':
      msg = 'Code Expired. Resend code if needed.';
      break;
    case 'auth/too-many-requests':
      msg = 'Action blocked. Too many requests.';
      break;
    case 'auth/invalid-phone-number':
      msg = 'Invalid phone number format. Make sure you start with + and your country code. (ex: +1 123 456 7890)';
      break;
    case 'auth/unverified-email':
      msg = 'Unverified email.';
      break;
    case 'auth/invalid-multi-factor-session':
      msg = 'MFA session expired. Please CANCEL and restart the process again.';
      break;
    case 'auth/missing-verification-id':
      msg = 'You have not verified the phone number.';
      break;
    case 'auth/requires-recent-login':
      msg = 'This action requires a recent login. Please Cancel and restart the process again.';
      break;
    default:
      msg = errorCode;
      noMatch = true;
    }

    if (custom && noMatch) {
      msg = `${errorCode}: ${custom}`;
      if (this.auth.currentUser) {
        sendToRaygun(msg, {
          firebaseUser: this.auth.currentUser.displayName ? this.auth.currentUser.displayName : 'N/A',
          firebaseEmail: this.auth.currentUser.email ? this.auth.currentUser.email : 'N/A',
        });
      } else {
        sendToRaygun(msg, {
          // Assuming here that user is in the login or other auth page because otherwise,
          // current user will be available.
          location: 'User Auth Pages',
        });
      }
    }

    return msg;
  }
}
